import React, { useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { BusinessProfile, MediaManagement, OpeningHoursManager } from "../../components/business";
import { useBusinessData } from "../../hooks/useBusinessData";
import { useMediaManagement } from "../../hooks/useMediaManagement";
import { defaultOpeningHours, OpeningHours } from "../../types/business";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import Sidebar from "../../components/layout/Sidebar";
import { useToast } from "../../components/ui/Toast/useToast";
import { NotificationPreferences } from "../../components/messaging";

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const {
    business,
    loading: businessLoading,
    editMode,
    form,
    setEditMode,
    handleInputChange,
    handleProfileUpdate,
    fetchBusinessData
  } = useBusinessData();

  // Media management hooks and state
  const {
    logo,
    setLogo,
    logoUploading,
    logoUrl,
    galleryImages,
    newGalleryImages,
    setNewGalleryImages,
    galleryUploading,
    handleLogoUpload,
    handleGalleryUpload,
    handleDeleteGalleryImage,
    handleReorderGallery,
    shopVideo,
    setShopVideo,
    videoUploading,
    videoDeleting,
    shopVideoUrl,
    handleVideoUpload,
    handleDeleteVideo
  } = useMediaManagement(business?.id, () => {
    // Refresh business data after media updates
    fetchBusinessData();
  });

  // Opening hours state - ensure we always use the proper OpeningHours type
  const [openingHours, setOpeningHours] = useState<OpeningHours>(() => {
    if (business?.business_hours && typeof business.business_hours === 'object') {
      // Convert the database format to our OpeningHours type if needed
      const parseHours = (hoursString: string | any) => {
        if (typeof hoursString === 'string') {
          if (hoursString === 'Closed') {
            return { open: '08:00', close: '17:00', closed: true };
          }
          const [open, close] = hoursString.split('-');
          return { open, close, closed: false };
        }
        return hoursString || defaultOpeningHours.monday;
      };

      return {
        monday: parseHours(business.business_hours.monday),
        tuesday: parseHours(business.business_hours.tuesday),
        wednesday: parseHours(business.business_hours.wednesday),
        thursday: parseHours(business.business_hours.thursday),
        friday: parseHours(business.business_hours.friday),
        saturday: parseHours(business.business_hours.saturday),
        sunday: parseHours(business.business_hours.sunday),
      };
    }
    return defaultOpeningHours;
  });
  const [selectedServices, setSelectedServices] = useState<string[]>(business?.services || []);

  // Sync selectedServices with business.services when business data loads/changes
  React.useEffect(() => {
    setSelectedServices(Array.isArray(business?.services) ? business.services : []);
  }, [business?.services]);

  // Sync openingHours with business.business_hours when business data loads/changes
  React.useEffect(() => {
    if (business?.business_hours && typeof business.business_hours === 'object') {
      const parseHours = (hoursString: string | any) => {
        if (typeof hoursString === 'string') {
          if (hoursString === 'Closed') {
            return { open: '08:00', close: '17:00', closed: true };
          }
          const [open, close] = hoursString.split('-');
          return { open, close, closed: false };
        }
        // If it's already an object, return as is
        return hoursString || defaultOpeningHours.monday;
      };

      setOpeningHours({
        monday: parseHours(business.business_hours.monday),
        tuesday: parseHours(business.business_hours.tuesday),
        wednesday: parseHours(business.business_hours.wednesday),
        thursday: parseHours(business.business_hours.thursday),
        friday: parseHours(business.business_hours.friday),
        saturday: parseHours(business.business_hours.saturday),
        sunday: parseHours(business.business_hours.sunday),
      });
    }
  }, [business?.business_hours]);
  const [hoursEditMode, setHoursEditMode] = useState(false);

  const handleOpeningHoursChange = (day: string, field: string, value: string | boolean) => {
    setOpeningHours(prev => ({
      ...prev,
      [day as keyof OpeningHours]: {
        ...prev[day as keyof OpeningHours],
        [field]: value
      }
    }));
  };

  const handleServicesChange = (service: string) => {
    setSelectedServices(prev => {
      const newServices = prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service];
      return newServices;
    });
  };

  const handleSaveHoursAndServices = async () => {
    try {
      if (!business?.id) {
        throw new Error("Business ID not found");
      }
      // Prepare opening hours as plain object for JSONB
      const formattedHours = Object.fromEntries(
        Object.entries(openingHours).map(([day, val]) => {
          // If val is an object with open/close/closed, flatten to string or closed
          if (typeof val === "object" && val !== null) {
            if (val.closed) return [day, "Closed"];
            return [day, `${val.open}-${val.close}`];
          }
          return [day, val];
        })
      );
      // Save to Supabase
      const { supabase } = await import("../../services/supabase");
      const { error } = await (supabase as any)
        .from("businesses")
        .update({
          business_hours: formattedHours,
          services: selectedServices
        })
        .eq("id", business.id);

      if (error) {
        throw error;
      }
      // Optionally show a toast here
      setHoursEditMode(false);
      // Refresh business data
      await fetchBusinessData();
    } catch (error) {
      console.error('Error saving hours and services:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      // Optionally handle logout error
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Navigation */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  Business Profile
                  {business && (
                    business.verified ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                        Pending Verification
                      </span>
                    )
                  )}
                </h1>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {user?.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            {/* Business Profile */}
            <ErrorBoundary fallback={<div className="p-4 bg-red-50 text-red-700 rounded-lg">Business profile unavailable</div>}>
              <BusinessProfile
                business={business}
                editMode={editMode}
                form={form}
                loading={businessLoading}
                onEditModeToggle={() => setEditMode(!editMode)}
                onInputChange={handleInputChange}
                onProfileUpdate={handleProfileUpdate}
                onCancel={() => setEditMode(false)}
              />
            </ErrorBoundary>

            {/* Media Management */}
            <ErrorBoundary fallback={<div className="p-4 bg-red-50 text-red-700 rounded-lg">Media management unavailable</div>}>
              <MediaManagement
                logoUrl={typeof business?.logo_url === 'string' ? business.logo_url : (logoUrl || "")}
                logo={logo}
                logoUploading={logoUploading}
                galleryImages={Array.isArray(business?.gallery) ? business.gallery.filter(img => typeof img === 'string') : (Array.isArray(galleryImages) ? galleryImages : [])}
                newGalleryImages={Array.isArray(newGalleryImages) ? newGalleryImages : []}
                galleryUploading={galleryUploading}
                shopVideoUrl={shopVideoUrl || ""}
                shopVideo={shopVideo}
                videoUploading={videoUploading}
                videoDeleting={videoDeleting}
                onLogoChange={setLogo}
                onLogoUpload={handleLogoUpload}
                onGalleryImagesChange={setNewGalleryImages}
                onGalleryUpload={handleGalleryUpload}
                onDeleteGalleryImage={handleDeleteGalleryImage}
                onReorderGallery={handleReorderGallery}
                onVideoChange={setShopVideo}
                onVideoUpload={handleVideoUpload}
                onVideoDelete={handleDeleteVideo}
              />
            </ErrorBoundary>

            {/* Opening Hours and Services */}
            <ErrorBoundary fallback={<div className="p-4 bg-red-50 text-red-700 rounded-lg">Opening hours management unavailable</div>}>
              <OpeningHoursManager
                openingHours={openingHours}
                selectedServices={selectedServices || []}
                editMode={hoursEditMode}
                loading={businessLoading}
                onHoursChange={handleOpeningHoursChange}
                onServicesChange={handleServicesChange}
                onSave={handleSaveHoursAndServices}
                onEditToggle={() => setHoursEditMode(!hoursEditMode)}
                onCancel={() => setHoursEditMode(false)}
                onSaveServices={async () => {
                  const toast = useToast();
                  try {
                    if (!business?.id) throw new Error("Business ID not found");
                    const { supabase } = await import("../../services/supabase");
                    const { error } = await (supabase as any)
                      .from("businesses")
                      .update({ services: selectedServices })
                      .eq("id", business.id);
                    if (error) throw error;
                    await fetchBusinessData();
                    toast.success("Services updated successfully");
                  } catch (error) {
                    toast.error("Failed to update services");
                    console.error('Error saving services:', error);
                  }
                }}
              />
            </ErrorBoundary>

            {/* Notification Preferences */}
            <ErrorBoundary fallback={<div className="p-4 bg-red-50 text-red-700 rounded-lg">Notification preferences unavailable</div>}>
              <NotificationPreferences
                className="bg-white rounded-lg shadow-sm border border-gray-200"
                showTitle={true}
              />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;